import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { CargoSpace, Group, WorkSpace } from '../types';
import { ColorPipe } from './../../shared/pipes/color.pipe';
import { getPalletLayerGroupsFromCargoSpace } from '../../shared/helpers/pallet-loading-space.helper';
import { ProjectService } from './project.service';

@Injectable({
  providedIn: 'root',
})
export class ColorsService {
  public colorIcons: Map<number, string> = new Map<number, string>();
  public currentWorkSpace = 0;
  public currentColor = 0x6aa84f;
  private lastColor: number;

  public baseColors: number[] = [
    0x895ef6, 0x6984ed, 0x4da1f7, 0x20cbdd, 0x7ae3aa, 0xffc63b, 0xff763e, 0xff61c7, 0xf12f63,
  ];

  public colorList = [
    0xa61c00, 0x990000, 0x85200c, 0xcc0000, 0xe06666, 0xcc4125, 0xb45f06, 0xbf9000, 0xe69138,
    0xf6b26b, 0xffd966, 0xf1c232, 0x93c47d, 0x6aa84f, 0x38761d, 0x3d85c6, 0x6d9eeb, 0x6fa8dc,
    0x76a5af, 0x45818e, 0x134f5c, 0x0b5394, 0x1155cc, 0x3c78d8, 0x351c75, 0x674ea7, 0x8e7cc3,
    0xc27ba0, 0xa64d79, 0x741b47, 0xffffff, 0xcccccc, 0x999999, 0x666666, 0x333333, 0x000000,
  ];

  private avaliableColors: number[] = [];

  constructor(private projectService: ProjectService, private colorPipe: ColorPipe) {
    this.projectService.currentWorkSpaceIndex.subscribe(
      (currentWorkSpace) => (this.currentWorkSpace = currentWorkSpace),
    );
    this.generateIconsWithBaseColors();
    this.filterAvaliableColors();
    this.generateIconsForRandomGeneratedColors();
  }

  public getColor(): number {
    let color: number;
    if (this.avaliableColors.length > 0) {
      color = this.avaliableColors[0];
      this.avaliableColors.splice(0, 1);
    } else {
      color = Math.floor(Math.random() * 16777215);
      this.generateLoadIcons(color);
    }
    if (this.lastColor === color) {
      this.getColor();
    }
    this.lastColor = color;
    return color;
  }

  public getAvaliableColor(): number {
    if (this.avaliableColors[0]) {
      return this.avaliableColors[0];
    } else {
      const color = Math.floor(Math.random() * 16777215);
      this.generateLoadIcons(color);
      this.baseColors.push(color);
      this.avaliableColors.push(color);
      return color;
    }
  }

  public getColorFromList(i: number) {
    return this.colorList[i];
  }

  public generateColorsList() {
    const colors: string[] = [];
    this.colorList.forEach((color) => {
      colors.push(this.colorPipe.transform(color));
    });
    return colors;
  }

  private generateIconsWithBaseColors(): void {
    this.generateLoadIcons(...this.baseColors);
  }

  private filterAvaliableColors(): void {
    this.getArrayOfGroupsColors().subscribe((colors) => {
      this.avaliableColors = this.baseColors.filter((c) => colors.indexOf(c) < 0);
    });
  }

  private generateIconsForRandomGeneratedColors(): void {
    this.getArrayOfGroupsColors().subscribe((colors) => {
      const randomColors: number[] = colors.filter((c) => this.baseColors.indexOf(c) < 0);
      this.generateLoadIcons(...randomColors);
    });
  }

  private getArrayOfGroupsColors(): Observable<number[]> {
    return this.projectService.project.pipe(
      pluck('workSpaces'),
      map((workSpaces: WorkSpace[]) => {
        if (!workSpaces || workSpaces?.length === 0) {
          return [];
        }

        const colors: number[] = [];
        workSpaces.forEach((workSpace) => {
          colors.push(...this.collectUsedColorsFromWorkSpace(workSpace));
        });
        return colors;
      }),
    );
  }

  private collectUsedColorsFromWorkSpace(workSpace: WorkSpace): number[] {
    const colors: number[] = [];
    const appendGroupColors = (groups: Group[] = []): void => {
      groups.forEach((group) => colors.push(group.color));
    };
    const appendPalletLayerColors = (cargoSpace: CargoSpace | undefined): void => {
      getPalletLayerGroupsFromCargoSpace(cargoSpace).forEach((palletGroup) => {
        colors.push(palletGroup.color);
        if (palletGroup.interlayer?.color != null) {
          colors.push(palletGroup.interlayer.color);
        }
      });
    };

    if (workSpace.cargoSpaces.length === 2) {
      appendGroupColors(workSpace.cargoSpaces[0].groups);
      appendGroupColors(workSpace.cargoSpaces[1].groups);
      appendPalletLayerColors(workSpace.cargoSpaces[0]);
      appendPalletLayerColors(workSpace.cargoSpaces[1]);
    } else {
      appendGroupColors(workSpace.cargoSpaces[0]?.groups);
      appendPalletLayerColors(workSpace.cargoSpaces[0]);
    }
    appendGroupColors(workSpace.outsideSpace?.groups);
    return colors;
  }

  private generateLoadIcons(...colors: number[]): string[] {
    const canvas = this.createCanvas();
    const context = canvas.getContext('2d');
    const icons: string[] = [];
    colors.forEach((color) => {
      this.drawCube(context, color);
      this.colorIcons.set(color, canvas.toDataURL('image/png'));
      context.clearRect(0, 0, canvas.width, canvas.height);
    });
    return icons;
  }

  private createCanvas(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    const width = 22;
    const height = 25;
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    return canvas;
  }

  public getDrawedCircle(color: number) {
    const canvas = document.createElement('canvas');
    const width = 20;
    const height = 20;
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    const context = canvas.getContext('2d');
    context.beginPath();
    context.arc(10, 10, 10, 0, 2 * Math.PI, true);
    context.fillStyle = this.colorPipe.transform(color);
    context.fill();
    const link = canvas.toDataURL('image/png');
    context.clearRect(0, 0, canvas.width, canvas.height);
    return link;
  }

  private drawCube(context: CanvasRenderingContext2D, color: number): void {
    context.beginPath();
    context.moveTo(0, 6.2);
    context.lineTo(0, 18.7);
    context.lineTo(11, 25);
    context.lineTo(22, 18.7);
    context.lineTo(22, 6.2);
    context.lineTo(11, 12.5);
    context.fillStyle = this.colorPipe.transform(color);
    context.fill();

    context.beginPath();
    context.moveTo(0, 6.2);
    context.lineTo(11, 12.5);
    context.lineTo(22, 6.2);
    context.lineTo(11, 0);
    context.fillStyle = this.lightenColor(this.colorPipe.transform(color), 20);
    context.fill();
  }

  private lightenColor(color: string, amt: number): string {
    let usePound = false;

    if (color[0] === '#') {
      color = color.slice(1);
      usePound = true;
    }

    const num = parseInt(color, 16);

    let r = (num >> 16) + amt;
    if (r > 255) {
      r = 255;
    } else if (r < 0) {
      r = 0;
    }

    let b = ((num >> 8) & 0x00ff) + amt;
    if (b > 255) {
      b = 255;
    } else if (b < 0) {
      b = 0;
    }

    let g = (num & 0x0000ff) + amt;
    if (g > 255) {
      g = 255;
    } else if (g < 0) {
      g = 0;
    }

    return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
  }

  public getMultistopImageWithDestination(destination: string, color: number, unloaded?: boolean) {
    const canvas = document.createElement('canvas');
    const width = 210;
    const height = 210;
    canvas.width = width;
    canvas.height = height;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    const context = canvas.getContext('2d');
    const colorTransformed = this.colorPipe.transform(color);

    context.beginPath();
    context.arc(80, 80, 80, 0, 2 * Math.PI, false);
    context.lineWidth = 10;
    context.fillStyle = colorTransformed;
    context.fill();
    context.strokeStyle = '#ffffff';
    context.stroke();

    context.font = 'bold 80px WorkSans, sans-serif';
    context.fillStyle = '#ffffff';
    context.fillText(destination.toUpperCase(), 50, 100);

    if (unloaded) {
      context.beginPath();
      context.moveTo(80, 210);
      context.lineTo(120, 160);
      context.lineTo(40, 160);
    } else {
      context.beginPath();
      context.moveTo(210, 80);
      context.lineTo(160, 120);
      context.lineTo(160, 40);
    }

    context.fillStyle = colorTransformed;
    context.fill();

    const link = canvas.toDataURL('image/png');
    context.clearRect(0, 0, canvas.width, canvas.height);
    return link;
  }
}
